import type { HomepageTrendingPost } from "@circle-react/components/HomePageV2/types";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { cleanEmptyProperties } from "@circle-react/helpers/cleanEmptyProperties";
import {
  homePagePostsPath,
  internalApi,
} from "@circle-react/helpers/urlHelpers";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { Post } from "@circle-react/types";
import type { BasicCommunityMember } from "@circle-react/types/CommunityMember";
import type { ContentReactionType } from "@circle-react/types/ContentReaction";
import type { PaginationHelper } from "@circle-react/types/InternalAPIHelper";
import { infiniteQueryOptions, queryOptions } from "react-query";

import {
  detailKeyRoot,
  listKeyRoot,
  postQueryKeys,
  queryKeyRoot,
} from "./constants";
import {
  getCurrentPageParam,
  getNextPageParam,
  joinBatchIds,
  matchPostDetailQuery,
} from "./helpers";
import type {
  BatchDetailsParams,
  DetailParams,
  FeedParams,
  PostBatchDetailsQueryParams,
  PostDetailRecord,
  PostListQueryOptions,
  PostPage,
  ProfileListParams,
  SpacePostsParams,
} from "./types";

export interface PostLikeMember extends BasicCommunityMember {
  reaction_type?: ContentReactionType;
}

type PostLikesPage = PaginationHelper<PostLikeMember>;

export const postQueries = {
  spacePosts: ({ spaceId, params }: SpacePostsParams): PostListQueryOptions => {
    const paramsPure = cleanEmptyProperties(params ?? {});
    const queryKey = spaceId
      ? listKeyRoot("space", spaceId, paramsPure)
      : listKeyRoot("space");

    return {
      queryKey,
      initialPageParam: 1,
      queryFn: ({ pageParam }) => {
        if (!spaceId) return Promise.reject(new Error("spaceId is required"));

        return reactQueryGet<PostPage>(
          internalApi.posts.index({
            spaceId,
            params: { ...paramsPure, page: getCurrentPageParam({ pageParam }) },
          }),
        );
      },
      getNextPageParam: getNextPageParam<PostPage>,
    };
  },

  feedPosts: ({ sort = "" }: FeedParams = {}): PostListQueryOptions => {
    const paramsConnection = { sort: "latest", only_connections: true };
    const params = sort === "connections" ? paramsConnection : { sort };
    const paramsPure = cleanEmptyProperties(params);
    const queryKey = [...postQueryKeys.feed, paramsPure] as const;

    return {
      queryKey,
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        reactQueryGet<PostPage>(
          internalApi.homePagePosts.index({
            params: {
              ...paramsPure,
              page: getCurrentPageParam({ pageParam }),
            },
          }),
        ),
      getNextPageParam: getNextPageParam<PostPage>,
    };
  },

  profilePosts: ({
    memberPublicId,
    params,
  }: ProfileListParams): PostListQueryOptions => {
    const paramsPure = cleanEmptyProperties(params ?? {});
    const queryKey = listKeyRoot("profile", memberPublicId, paramsPure);

    return {
      queryKey,
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        reactQueryGet<PostPage>(
          internalApi.profile.posts({
            memberPublicId,
            params: { ...paramsPure, page: getCurrentPageParam({ pageParam }) },
          }),
        ),
      getNextPageParam: getNextPageParam<PostPage>,
    };
  },

  postDetails: ({ spaceId, spaceSlug = "", postSlug = "" }: DetailParams) =>
    queryOptions({
      queryKey: detailKeyRoot("record", spaceId ?? spaceSlug, postSlug),
      queryFn: () =>
        reactQueryGet<Post>(
          internalApi.posts.show({ spaceId, spaceSlug, postSlug }),
        ),
    }),

  postLikes: ({
    postId,
    reactionType,
  }: {
    postId: number;
    reactionType?: ContentReactionType | null;
  }) =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: queryKeyRoot.key("likes", postId, reactionType ?? "all"),
      queryFn: context =>
        reactQueryGet<PostLikesPage>(
          internalApi.posts.userLikesList({
            postId,
            params: {
              per_page: 15,
              page: getCurrentPageParam(context),
              ...(reactionType && { reaction_type: reactionType }),
            },
          }),
        ),
      getNextPageParam: getNextPageParam<PostLikesPage>,
    }),

  trendingPosts: () =>
    queryOptions({
      queryKey: queryKeyRoot.key("trending"),
      queryFn: async () => {
        const { posts } = await reactQueryGet<{
          posts: HomepageTrendingPost[];
        }>(homePagePostsPath());

        return posts;
      },
    }),

  postBatchDetails: ({
    isSharedPostView,
    postIds,
    spaceId,
  }: BatchDetailsParams) => {
    const params: PostBatchDetailsQueryParams = {
      for_shared_post_view: isSharedPostView || undefined,
      post_ids: joinBatchIds(postIds),
      space_id: spaceId,
    };

    const batchKey = isSharedPostView ? "shared-batch" : "batch";
    const paramsPure = cleanEmptyProperties(params);

    return {
      queryKey: detailKeyRoot(batchKey, paramsPure),
      queryFn: () =>
        reactQueryGet<PostDetailRecord[]>(
          internalApi.postDetails.index({ params: { ...paramsPure } }),
        ),
    };
  },
};

export const postInvalidations = {
  all: () => queryClient.invalidateQueries({ queryKey: queryKeyRoot.root() }),

  feedPosts: () =>
    queryClient.invalidateQueries({ queryKey: postQueryKeys.feed }),

  trendingPosts: () =>
    queryClient.invalidateQueries({ queryKey: queryKeyRoot.key("trending") }),

  spacePosts: (spaceId: number) =>
    queryClient.invalidateQueries({ queryKey: listKeyRoot("space", spaceId) }),

  allSpacePosts: () =>
    queryClient.invalidateQueries({ queryKey: listKeyRoot("space") }),

  profilePosts: (memberPublicId: string) =>
    queryClient.invalidateQueries({
      queryKey: listKeyRoot("profile", memberPublicId),
    }),

  // Invalidates all post list queries (space + feed + profile).
  allPosts: () =>
    queryClient.invalidateQueries({ queryKey: postQueryKeys.list }),

  // Invalidates record + batch/shared-batch for a specific post.
  postDetails: (postId: number) =>
    queryClient.invalidateQueries({
      queryKey: postQueryKeys.detail,
      predicate: matchPostDetailQuery(postId),
    }),

  // Invalidates all post detail queries (record + batch/shared-batch).
  allPostDetails: () =>
    queryClient.invalidateQueries({ queryKey: postQueryKeys.detail }),

  postLikes: (postId: number) =>
    queryClient.invalidateQueries({
      queryKey: queryKeyRoot.key("likes", postId),
    }),
};
